// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, setOnUnauthorized } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) return null;
        
        // Validate token immediately during initialization
        try {
            const payload = JSON.parse(atob(storedToken.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
                // Token expired, don't use it
                console.warn('Token expired during initialization, removing');
                localStorage.removeItem('token');
                return null;
            }
            return storedToken;
        } catch (e) {
            // Invalid token format
            console.warn('Invalid token format during initialization, removing');
            localStorage.removeItem('token');
            return null;
        }
    });
    const [idleLogoutInfo, setIdleLogoutInfo] = useState(null); // { reason: 'idle' | 'unauthorized' }
    const idleTimerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

    const clearAuth = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    }, []);

    const loadProfile = useCallback(async (activeToken, skipIfUserExists = false) => {
        if (!activeToken) {
            setLoading(false);
            return;
        }
        // Skip profile fetch if user already exists (e.g., just logged in)
        if (skipIfUserExists && user) {
            setLoading(false);
            return;
        }
        try {
            const response = await authAPI.getProfile(activeToken);
            // Accept multiple possible response envelopes:
            // - { data: { user: { ... } }, message: '' }
            // - { data: { ...user fields... }, message: '' }
            // - { user: { ... } }
            const profile = response && response.data ? (response.data.user || response.data.data || response.data) : null;
            setUser(profile || null);
            // (debug logging removed)
        } catch (err) {
            console.error('Profile load error:', err);
            if (err.status === 401 || err.status === 422) {
                clearAuth();
            }
        } finally {
            setLoading(false);
        }
    }, [clearAuth, user]);

    useEffect(() => {
        // Only load profile on mount or if token changes but we don't have user data
        loadProfile(token, true);
    }, [token, loadProfile]);

    // Global 401 auto-logout hook
    useEffect(() => {
        setOnUnauthorized(() => {
            if (!localStorage.getItem('token')) return;
            setIdleLogoutInfo({ reason: 'unauthorized' });
            clearAuth();
        });
        return () => setOnUnauthorized(null);
    }, [clearAuth]);

    // Idle detection: logout after 5 minutes of inactivity
    useEffect(() => {
        if (!token) return;

        const isAnyVideoPlaying = () => {
            try {
                const videos = document.querySelectorAll('video');
                for (const v of videos) {
                    if (!v.paused && !v.ended && v.readyState >= 2) {
                        return true;
                    }
                }
            } catch (_) {}
            return false;
        };

        const markActivity = () => {
            lastActivityRef.current = Date.now();
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            idleTimerRef.current = setTimeout(() => {
                // If any video is actively playing, treat as activity and extend session
                if (isAnyVideoPlaying()) {
                    markActivity();
                    return;
                }
                const inactiveFor = Date.now() - lastActivityRef.current;
                if (inactiveFor >= IDLE_TIMEOUT_MS) {
                    setIdleLogoutInfo({ reason: 'idle' });
                    clearAuth();
                }
            }, IDLE_TIMEOUT_MS);
        };

        const events = ['mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'];
        events.forEach(evt => window.addEventListener(evt, markActivity, { passive: true }));
        const onVisibility = () => { if (!document.hidden) markActivity(); };
        document.addEventListener('visibilitychange', onVisibility);
        markActivity();

        return () => {
            events.forEach(evt => window.removeEventListener(evt, markActivity));
            document.removeEventListener('visibilitychange', onVisibility);
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        };
    }, [token, clearAuth]);

    const login = async (email, password, role) => {
        const response = await authAPI.login(email, password, role);
        const { access_token } = response.data;
        // Set token in localStorage first
        localStorage.setItem('token', access_token);
        setToken(access_token);
        // Always fetch latest profile from profile API after login
        let finalUser = null;
        try {
            const profileRes = await authAPI.getProfile(access_token);
            finalUser = profileRes && profileRes.data ? (profileRes.data.user || profileRes.data.data || profileRes.data) : null;
            setUser(finalUser || null);
        } catch (err) {
            setUser(null);
        }
        
        if (finalUser && finalUser.role === 'instructor' && (finalUser.planType || finalUser.plan_type || 'free').toLowerCase() === 'free') {
            const pendingPlan = localStorage.getItem('pending_subscription_plan');
            if (pendingPlan) {
                localStorage.removeItem('pending_subscription_plan');
                localStorage.setItem('redirecting_to_checkout', 'true');
                window.location.href = `/payments/checkout/${pendingPlan}`;
            }
        }
        setLoading(false);
        return response;
    };

    // Finalizes a session from an already-issued access token (e.g. after the
    // super-admin OTP step succeeds) without re-running the password check --
    // just stores the token and loads the profile, same as the tail end of login().
    const completeLogin = async (accessToken) => {
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
        let finalUser = null;
        try {
            const profileRes = await authAPI.getProfile(accessToken);
            finalUser = profileRes && profileRes.data ? (profileRes.data.user || profileRes.data.data || profileRes.data) : null;
            setUser(finalUser || null);
        } catch (err) {
            setUser(null);
        }
        setLoading(false);
        return finalUser;
    };

    const register = async (userData) => {
        const response = await authAPI.register(userData);
        const { access_token, user } = response.data || {};
        let redirecting = false;
        // Only set token if backend returned one (e.g., after verification flows)
        if (access_token && user) {
            localStorage.setItem('token', access_token);
            setToken(access_token);
            setUser(user);

            if (user && user.role === 'instructor' && (user.planType || user.plan_type || 'free').toLowerCase() === 'free') {
                const pendingPlan = localStorage.getItem('pending_subscription_plan');
                if (pendingPlan) {
                    localStorage.removeItem('pending_subscription_plan');
                    localStorage.setItem('redirecting_to_checkout', 'true');
                    window.location.href = `/payments/checkout/${pendingPlan}`;
                    redirecting = true;
                }
            }
        }
        return { ...response, redirecting }; // allow caller to inspect verificationSent/message and redirecting
    };

    const verifyEmail = async (tokenFromLink) => {
        const response = await authAPI.verifyEmail(tokenFromLink);
        const { access_token, user } = response.data;
        let redirecting = false;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(user);

        if (user && user.role === 'instructor' && (user.planType || user.plan_type || 'free').toLowerCase() === 'free') {
            const pendingPlan = localStorage.getItem('pending_subscription_plan');
            if (pendingPlan) {
                localStorage.removeItem('pending_subscription_plan');
                window.location.href = `/payments/checkout/${pendingPlan}`;
                redirecting = true;
            }
        }

        return { ...response, redirecting };
    };

    const googleLogin = async (credential, role = null, plan = 'free') => {
        try {
            const response = await authAPI.googleVerify(credential, role, plan);
            const { access_token, user: userData, isNewUser, needsRole, tempData, linked } = response.data;

            // Handle case where role selection is needed for new Google users
            if (needsRole) {
                // Throw error with tempData so the component can handle role selection
                const error = new Error(response.data.message || 'Role selection required');
                error.needsRole = true;
                error.tempData = tempData;
                throw error;
            }

            // Enforce portal role: if a role was selected on the login page
            // and the existing account has a different role, block sign-in
            // with the same message used for email/password login.
            if (role && userData?.role && userData.role !== role) {
                const mismatchError = new Error(
                    `This account is registered as '${userData.role}'. Please switch to the correct portal.`
                );
                throw mismatchError;
            }

            // Successful authentication
            localStorage.setItem('token', access_token);
            setToken(access_token);
            
            let finalUser = userData;
            try {
                const profileRes = await authAPI.getProfile(access_token);
                const profile = profileRes && profileRes.data ? (profileRes.data.user || profileRes.data.data || profileRes.data) : null;
                if (profile) {
                    finalUser = profile;
                }
            } catch (err) {
                // fallback to userData if profile fetch fails
            }
            setUser(finalUser);

            if (finalUser && finalUser.role === 'instructor' && (finalUser.planType || finalUser.plan_type || 'free').toLowerCase() === 'free') {
                const pendingPlan = localStorage.getItem('pending_subscription_plan');
                if (pendingPlan) {
                    localStorage.removeItem('pending_subscription_plan');
                    localStorage.setItem('redirecting_to_checkout', 'true');
                    window.location.href = `/payments/checkout/${pendingPlan}`;
                }
            }

            return {
                user: finalUser,
                isNewUser: isNewUser || false,
                linked: linked || false
            };
        } catch (error) {
            // If it's our custom needsRole error, re-throw it
            if (error.needsRole) {
                throw error;
            }

            // Handle API errors
            if (error.response) {
                const apiError = new Error(
                    error.response.data?.message || 'Google sign-in failed'
                );
                apiError.status = error.response.status;
                
                // Check if role selection is needed from API response
                if (error.response.data?.needsRole) {
                    apiError.needsRole = true;
                    apiError.tempData = error.response.data.tempData;
                }
                
                throw apiError;
            }

            // Network or other errors
            throw new Error(error.message || 'Google sign-in failed. Please try again.');
        }
    };

    const unlinkGoogle = async () => {
        if (!token) {
            throw new Error('Not authenticated');
        }

        try {
            const response = await authAPI.unlinkGoogle(token);
            
            // Update user state to reflect unlinked Google account
            if (response.data?.user) {
                setUser(response.data.user);
            }

            return response;
        } catch (error) {
            if (error.response) {
                throw new Error(
                    error.response.data?.message || 'Failed to unlink Google account'
                );
            }
            throw new Error('Failed to unlink Google account. Please try again.');
        }
    };

    const logout = () => {
        clearAuth();
    };

    const subscribe = async (plan, months) => {
        if (!token) throw new Error('Not authenticated');
        const res = await authAPI.subscribe(token, plan, months);
        // Update user plan in state
        if (res?.data) {
            const returnedUser = res.data.user || null;
            const subscription = res.data.subscription || null;
            if (returnedUser) {
                // merge subscription object into user state so frontend can show exact plan
                const merged = subscription ? { ...returnedUser, subscription } : returnedUser;
                setUser(merged);
            } else {
                // Ensure we reload profile when backend doesn't return updated user
                try {
                    await loadProfile(token, false);
                } catch (e) {
                    // ignore - fallback to existing state
                }
            }
        }
        return res;
    };

    const needsSubscription = !!user && user.role === 'instructor' && (!user.planType && !user.plan_type || (user.planType || user.plan_type) === 'free');

    const value = {
        user,
        token,
        login,
        completeLogin,
        register,
        googleLogin,
        unlinkGoogle,
        logout,
        subscribe,
        verifyEmail,
        loading,
        isAuthenticated: !!user,
        needsSubscription,
        reloadProfile: () => loadProfile(localStorage.getItem('token')),
        updateUser: (patch) => setUser(prev => prev ? { ...prev, ...patch } : prev),
        idleLogoutInfo,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;